/**
 * Shared-Service Cost Allocation Engine — the "Google Sheets killer".
 *
 * Why this page exists
 * --------------------
 * Every FinOps engineer has a recurring nightmare: a Redis cluster, a
 * Snowflake warehouse, or a Kafka topic serves N teams. The bill shows
 * ONE line item. The CFO wants per-team chargeback. So they open Google
 * Sheets, build a weighted formula, and email out PDFs every month.
 *
 * This page replaces that spreadsheet with a deterministic, auditable
 * engine — with 6 strategies (even / weighted / per_user / per_call /
 * per_byte / tag_match) and a live $-amount preview that reconciles to
 * the cent.
 *
 * UX flow
 * -------
 *   Step 1 — pick a strategy chip
 *   Step 2 — name the shared service + total $ amount
 *   Step 3 — add 2-N targets (teams / features / customers); fill in the
 *            attribute the strategy needs (seat count, request count, etc.)
 *   Step 4 — watch the live preview reconcile in green
 *   Step 5 — Save Rule → re-apply each month from the History list
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Plus, Trash2, Sparkles, Check, AlertCircle, FileSpreadsheet, ArrowLeft,
  History as HistoryIcon, Layers, DollarSign, Calculator, Download, Cloud,
} from 'lucide-react';
import BrandTitle from '../components/BrandTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STRATEGY_FIELDS = {
  even: [],
  weighted: ['weight'],
  per_user: ['seat_count'],
  per_call: ['request_count'],
  per_byte: ['bytes_transferred'],
  tag_match: ['usage_share'],
};

const fmtUSD = (n) =>
  '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CostAllocation = () => {
  const [strategies, setStrategies] = useState([]);
  const [rules, setRules] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Rule builder state.
  const [name, setName] = useState('');
  const [source, setSource] = useState('');
  const [totalCost, setTotalCost] = useState(1200);
  const [strategy, setStrategy] = useState('per_call');
  const [targets, setTargets] = useState([
    { target_id: 'chat', target_label: 'Chat service', request_count: 40000 },
    { target_id: 'search', target_label: 'Search', request_count: 15000 },
    { target_id: 'jobs', target_label: 'Background jobs', request_count: 5000 },
  ]);
  const [preview, setPreview] = useState(null);

  const token = useMemo(() => localStorage.getItem('token') || '', []);
  const authHeader = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token],
  );

  // ── Load strategies + existing rules ─────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const [stratsRes, rulesRes, historyRes] = await Promise.all([
          axios.get(`${API}/allocation/strategies`),
          axios.get(`${API}/allocation/rules`, { headers: authHeader }).catch(() => ({ data: { rules: [] } })),
          axios.get(`${API}/allocation/history?limit=20`, { headers: authHeader }).catch(() => ({ data: { history: [] } })),
        ]);
        setStrategies(stratsRes.data.strategies || []);
        setRules(rulesRes.data.rules || []);
        setHistory(historyRes.data.history || []);
      } catch (e) {
        // Stays in defaults — non-fatal.
      } finally {
        setLoading(false);
      }
    })();
  }, [authHeader]);

  // ── Live preview — re-runs on every change ───────────────────────
  useEffect(() => {
    if (!strategy || !targets.length) return;
    const handle = setTimeout(async () => {
      try {
        const res = await axios.post(
          `${API}/allocation/preview`,
          { strategy, total_cost: Number(totalCost) || 0, targets },
          { headers: authHeader },
        );
        setPreview(res.data);
      } catch (e) {
        setPreview(null);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [strategy, totalCost, targets, authHeader]);

  const updateTarget = (i, field, value) => {
    setTargets((prev) => prev.map((t, idx) => (idx === i ? { ...t, [field]: value } : t)));
  };

  const removeTarget = (i) => setTargets((prev) => prev.filter((_, idx) => idx !== i));

  const addTarget = () => setTargets((prev) => [
    ...prev,
    { target_id: `team-${prev.length + 1}`, target_label: `Team ${prev.length + 1}`, weight: 1, request_count: 1000, seat_count: 5, bytes_transferred: 1000000, usage_share: 1 },
  ]);

  const saveRule = async () => {
    if (!name.trim() || !source.trim()) {
      toast.error('Name + source are required');
      return;
    }
    if (targets.length < 2) {
      toast.error('At least 2 targets required');
      return;
    }
    try {
      const res = await axios.post(
        `${API}/allocation/rules`,
        { name, source, strategy, targets },
        { headers: authHeader },
      );
      setRules((prev) => [res.data, ...prev]);
      toast.success(`Rule "${name}" saved`);
      setName('');
      setSource('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save rule');
    }
  };

  const applyRule = async (rule) => {
    try {
      const res = await axios.post(
        `${API}/allocation/apply`,
        { rule_id: rule.rule_id, total_cost: Number(totalCost) || 0, period: new Date().toISOString().slice(0, 7) },
        { headers: authHeader },
      );
      setHistory((prev) => [res.data, ...prev]);
      toast.success(`Applied "${rule.name}" — ${fmtUSD(res.data.total_cost)} split`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to apply rule');
    }
  };

  const deleteRule = async (rule) => {
    try {
      await axios.delete(`${API}/allocation/rules/${rule.rule_id}`, { headers: authHeader });
      setRules((prev) => prev.filter((r) => r.rule_id !== rule.rule_id));
      toast.success('Rule deleted');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const exportCSV = () => {
    if (!preview?.splits?.length) return;
    const rows = [
      ['target_id', 'target_label', 'share_pct', 'amount_usd'],
      ...preview.splits.map((s) => [s.target_id, s.target_label, s.share_pct, s.amount]),
    ];
    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cartie-allocation-${source || 'preview'}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  };

  const needsField = STRATEGY_FIELDS[strategy]?.[0];

  // ─── Connector status: shows Live vs Demo seed badge on each source ───
  const [connectorStatus, setConnectorStatus] = useState({});
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/allocation/connector-status`, { headers: authHeader })
      .then((res) => setConnectorStatus(res.data.sources || {}))
      .catch(() => {});
  }, [token, authHeader]);

  // ─── Auto-import: pull this month's actual usage from a connected source ─
  const [importing, setImporting] = useState(null);
  const autoImport = async (source) => {
    setImporting(source);
    try {
      const res = await axios.get(
        `${API}/allocation/auto-import/${source}`,
        { headers: authHeader },
      );
      const { strategy: s, total_cost, targets: t, period, row_count, note } = res.data;
      setStrategy(s);
      setTotalCost(total_cost);
      setTargets(t);
      // Auto-fill source label too so the rule is named meaningfully.
      const sourceLabel = {
        ai_features: 'AI features (per feature_slug)',
        ai_customers: 'AI cost per customer',
        aws: 'AWS account-level',
        azure: 'Azure subscription-level',
        gcp: 'GCP project-level',
        snowflake: 'Snowflake warehouse usage',
        databricks: 'Databricks workspaces',
        digitalocean: 'DigitalOcean projects',
      }[source] || source;
      setSource(sourceLabel);
      if (!name.trim()) setName(`Auto · ${sourceLabel} · ${period}`);
      toast.success(`Imported ${row_count} buckets · ${period} · ${fmtUSD(total_cost)}`);
      if (note) toast.info(note, { duration: 5500 });
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to import from ${source}`);
    } finally {
      setImporting(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-100">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="cost-allocation-page">
      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 text-xs text-slate-600 mb-2">
              <Link to="/dashboard" className="hover:text-emerald-400 flex items-center gap-1 font-medium" data-testid="alloc-back-link">
                <ArrowLeft className="w-3 h-3" /> Dashboard
              </Link>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" data-testid="alloc-h1"><BrandTitle>Cost Allocation Engine</BrandTitle></h1>
            <p className="text-slate-600 mt-2 max-w-2xl">
              Replace the monthly chargeback spreadsheet. Split a shared service's bill across teams/features/customers using 6 deterministic strategies — and reconcile to the cent every time.
            </p>
          </div>
          <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 font-bold tracking-wide" data-testid="alloc-google-sheets-badge">
            <FileSpreadsheet className="w-3 h-3 mr-1.5" /> Google-Sheets killer
          </Badge>
        </div>

        {/* ─── Auto-import row — the "1-click pull this month's bill" UX ─── */}
        <Card className="mb-6 bg-slate-900 bg-gradient-to-r from-emerald-500/5 via-slate-900 to-cyan-500/5 border-emerald-500/30 text-slate-100" data-testid="alloc-autoimport-card">
          <CardContent className="p-4 sm:p-5">
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-300 mb-1">
                    <Sparkles className="w-3 h-3 mr-1 inline" /> Skip the typing
                  </div>
                  <div className="text-sm text-slate-300">
                    Pull this month's actual usage from a connected source. We'll fill the strategy, total, and targets for you.
                  </div>
                </div>
                <Link to="/cloud-integrations" className="text-xs text-emerald-400 hover:text-emerald-300 underline whitespace-nowrap shrink-0" data-testid="alloc-connect-link">
                  Connect more →
                </Link>
              </div>
              <div className="flex flex-wrap items-center gap-2" data-testid="alloc-autoimport-buttons">
                {[
                  { id: 'ai_features', label: 'AI features', icon: Sparkles },
                  { id: 'ai_customers', label: 'AI customers', icon: Sparkles },
                  { id: 'aws', label: 'AWS accounts', icon: Cloud },
                  { id: 'azure', label: 'Azure subs', icon: Cloud },
                  { id: 'gcp', label: 'GCP projects', icon: Cloud },
                  { id: 'snowflake', label: 'Snowflake', icon: Cloud },
                  { id: 'databricks', label: 'Databricks', icon: Cloud },
                  { id: 'digitalocean', label: 'DigitalOcean', icon: Cloud },
                ].map((src) => {
                  const status = connectorStatus[src.id];
                  const isLive = status?.live === true;
                  return (
                    <Button
                      key={src.id}
                      size="sm"
                      variant="outline"
                      onClick={() => autoImport(src.id)}
                      disabled={importing === src.id}
                      className={`border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 relative ${isLive ? 'ring-1 ring-emerald-400/40' : ''}`}
                      data-testid={`alloc-autoimport-${src.id}`}
                    >
                      <Download className="w-3 h-3 mr-1.5" />
                      {importing === src.id ? 'Pulling…' : src.label}
                      {/* Live/Demo dot — honest signal next to every source */}
                      <span
                        className={`ml-2 inline-flex items-center text-[11px] font-bold tracking-wider uppercase rounded px-1.5 py-0.5 ${
                          isLive
                            ? 'bg-emerald-500/25 text-emerald-200'
                            : 'bg-slate-700 text-slate-200'
                        }`}
                        title={isLive ? 'Connected to real data' : 'Showing deterministic demo seed — connect via /cloud-integrations'}
                        data-testid={`alloc-status-${src.id}`}
                      >
                        {isLive ? 'LIVE' : 'DEMO'}
                      </span>
                    </Button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* ─── LEFT: rule builder ─── */}
          <div className="lg:col-span-3 space-y-5">
            <Card className="bg-slate-900 text-slate-100 border-slate-800">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2 text-white">
                  <Layers className="w-4 h-4 text-emerald-400" /> Step 1 — Pick a strategy
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2" data-testid="alloc-strategy-chips">
                  {strategies.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setStrategy(s.id)}
                      data-testid={`alloc-strategy-${s.id}`}
                      className={`px-3 py-2 rounded-md border text-xs font-semibold transition-all ${
                        strategy === s.id
                          ? 'bg-emerald-500/20 border-emerald-500/60 text-emerald-300'
                          : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-emerald-500/40'
                      }`}
                    >
                      {s.label}
                      <span className="block text-[11px] text-slate-400 font-normal mt-0.5">{s.best_for}</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900 text-slate-100 border-slate-800">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2 text-white">
                  <DollarSign className="w-4 h-4 text-emerald-400" /> Step 2 — Name + total cost
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-400">Rule name</Label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Shared Redis chargeback"
                      data-testid="alloc-name-input"
                      className="bg-slate-950 border-slate-700"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400">Source service</Label>
                    <Input
                      value={source}
                      onChange={(e) => setSource(e.target.value)}
                      placeholder="e.g. prod-redis-cluster"
                      data-testid="alloc-source-input"
                      className="bg-slate-950 border-slate-700"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Total monthly $ to allocate</Label>
                  <Input
                    type="number"
                    value={totalCost}
                    onChange={(e) => setTotalCost(e.target.value)}
                    data-testid="alloc-total-cost-input"
                    className="bg-slate-950 border-slate-700 font-mono"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900 text-slate-100 border-slate-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base text-white">Step 3 — Targets ({targets.length})</CardTitle>
                  <Button size="sm" onClick={addTarget} variant="outline" className="border-emerald-500/40 hover:bg-emerald-500/10" data-testid="alloc-add-target-btn">
                    <Plus className="w-3 h-3 mr-1" /> Add target
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {targets.map((t, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`alloc-target-row-${i}`}>
                    <div className="col-span-4">
                      <Label className="text-[11px] text-slate-400 font-semibold">ID</Label>
                      <Input
                        value={t.target_id}
                        onChange={(e) => updateTarget(i, 'target_id', e.target.value)}
                        className="bg-slate-950 border-slate-700 h-8 text-xs"
                      />
                    </div>
                    <div className="col-span-4">
                      <Label className="text-[11px] text-slate-400 font-semibold">Label</Label>
                      <Input
                        value={t.target_label || ''}
                        onChange={(e) => updateTarget(i, 'target_label', e.target.value)}
                        className="bg-slate-950 border-slate-700 h-8 text-xs"
                      />
                    </div>
                    {needsField && (
                      <div className="col-span-3">
                        <Label className="text-[11px] text-slate-400 font-semibold">{needsField}</Label>
                        <Input
                          type="number"
                          value={t[needsField] ?? 0}
                          onChange={(e) => updateTarget(i, needsField, Number(e.target.value))}
                          className="bg-slate-950 border-slate-700 h-8 text-xs font-mono"
                          data-testid={`alloc-target-field-${i}`}
                        />
                      </div>
                    )}
                    <div className={needsField ? 'col-span-1' : 'col-span-4'}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTarget(i)}
                        disabled={targets.length <= 2}
                        className="text-rose-400 hover:bg-rose-500/10 h-8 w-full"
                        data-testid={`alloc-remove-target-${i}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button onClick={saveRule} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold" data-testid="alloc-save-rule-btn">
                <Check className="w-4 h-4 mr-2" /> Save rule
              </Button>
              <Button onClick={exportCSV} variant="outline" className="border-slate-700 text-slate-600" data-testid="alloc-export-csv-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Export current split as CSV
              </Button>
            </div>
          </div>

          {/* ─── RIGHT: live preview ─── */}
          <div className="lg:col-span-2 space-y-5">
            <Card className="bg-emerald-500/5 border-emerald-500/40 sticky top-4" data-testid="alloc-preview-card">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2 text-slate-900">
                  <Sparkles className="w-4 h-4 text-emerald-400" /> Live preview
                </CardTitle>
              </CardHeader>
              <CardContent>
                {preview ? (
                  <>
                    <div className="space-y-2 mb-4" data-testid="alloc-preview-splits">
                      {preview.splits.map((s, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm border-b border-slate-800/60 pb-2 last:border-0">
                          <div>
                            <div className="text-slate-700 font-medium">{s.target_label}</div>
                            <div className="text-[11px] text-slate-400">{s.share_pct}% share</div>
                          </div>
                          <div className="text-right tabular-nums font-mono text-emerald-300 font-bold">{fmtUSD(s.amount)}</div>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-emerald-500/30">
                      <div className="text-xs text-slate-400">Total allocated</div>
                      <div className="font-mono font-bold text-slate-900">{fmtUSD(preview.total_allocated)}</div>
                    </div>
                    {preview.reconciles ? (
                      <div className="mt-2 flex items-center gap-1.5 text-[11px] text-emerald-300">
                        <Check className="w-3 h-3" /> Reconciles to the cent ✓
                      </div>
                    ) : (
                      <div className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-400">
                        <AlertCircle className="w-3 h-3" /> Does not reconcile (input ≠ output)
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-xs text-slate-400">Fill in the rule to see the live split…</div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-slate-900 text-slate-100 border-slate-800" data-testid="alloc-saved-rules-card">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2 text-white">
                  <Calculator className="w-4 h-4 text-slate-400" /> Saved rules ({rules.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-72 overflow-y-auto">
                {rules.length === 0 && (
                  <p className="text-xs text-slate-400">No saved rules yet. Configure one on the left and hit Save.</p>
                )}
                {rules.map((r) => (
                  <div key={r.rule_id} className="flex items-center justify-between border border-slate-700 bg-slate-900 rounded-md p-2.5">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-300 truncate">{r.name}</div>
                      <div className="text-xs text-slate-400 truncate">{r.source} · {r.strategy} · {r.targets?.length} targets</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => applyRule(r)} className="h-7 px-2 text-emerald-400" data-testid={`alloc-apply-${r.rule_id}`}>
                        Apply
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteRule(r)} className="h-7 px-2 text-rose-400" data-testid={`alloc-delete-${r.rule_id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="bg-slate-900 text-slate-100 border-slate-800" data-testid="alloc-history-card">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2 text-white">
                  <HistoryIcon className="w-4 h-4 text-slate-400" /> Recent allocations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-60 overflow-y-auto">
                {history.length === 0 && <p className="text-xs text-slate-400">No history yet.</p>}
                {history.map((h) => (
                  <div key={h.history_id} className="text-xs border-l-2 border-emerald-500/50 pl-3 py-1">
                    <div className="font-medium text-slate-400">{h.rule_name} · {fmtUSD(h.total_cost)}</div>
                    <div className="text-slate-400">{h.period || '—'} · {h.applied_at?.slice(0, 16).replace('T', ' ')}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostAllocation;
